<template>
  <div class="wizard-container">
    <div class="wizard-steps">
      <!-- Step with connectors -->
      <div class="steps-row">
        <!-- Left edge circle – hidden on mobile -->
        <div class="step-wrapper edge-mobile-hide">
          <div class="step-circle edge completed"></div>
        </div>

        <template v-for="(step, index) in steps" :key="index">
          <!-- Connector -->
          <div
            class="connector"
            :class="{ completed: index <= currentStep, 'first-connector': index === 0 }"
          ></div>

          <!-- Step -->
          <div class="step-wrapper">
            <div
              class="step-circle"
              :class="{
                completed: index < currentStep || (isFinalStep(index) && finalStepIsComplete),
                current: index === currentStep && !(isFinalStep(index) && finalStepIsComplete)
              }"
            >
              <span v-if="index < currentStep">✔</span>
              <span v-else-if="isFinalStep(index) && finalStepIsComplete">✔</span>
              <span v-else-if="index === currentStep">✎</span>
              <span v-else>{{ index + 1 }}</span>
            </div>
          </div>
        </template>

        <!-- Right edge circle – hidden on mobile -->
        <div class="connector edge-mobile-hide" :class="{ completed: currentStep === steps.length - 1 && finalStepIsComplete }"></div>
        <div class="step-wrapper edge-mobile-hide">
          <div class="step-circle edge" :class="{ completed: currentStep === steps.length - 1 && finalStepIsComplete }"></div>
        </div>
      </div>

      <!-- Labels row -->
      <div class="labels-row">
        <div class="label-wrapper edge-mobile-hide">
          <div class="label placeholder-label"></div>
        </div>

        <template v-for="(step, index) in steps" :key="'label-' + index">
          <div class="connector-placeholder"></div>
          <div class="label-wrapper">
            <div
              class="label"
              :class="{
                completed:
                  index < currentStep ||
                  (isFinalStep(index) && finalStepIsComplete) ||
                  index === currentStep
              }"
            >
              {{ step }}
            </div>
          </div>
        </template>

        <div class="connector-placeholder edge-mobile-hide"></div>
        <div class="label-wrapper edge-mobile-hide">
          <div class="label placeholder-label"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  steps: Array,
  currentStep: Number,
  finalStepIsComplete: Boolean
});

function isFinalStep(index) {
  return index === props.steps.length - 1 && index === props.currentStep;
}
</script>

<style scoped>
.wizard-container {
  width: 100%;
  padding: 2rem 1rem;
}

.wizard-steps {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.steps-row {
  display: flex;
  align-items: center;
  position: relative;
  width: 100%;
  overflow-x: auto;
  white-space: nowrap;
}

.step-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.step-circle {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #004650;
  background: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 14px;
  color: #004650;
  z-index: 2;
}

.step-circle.completed {
  background-color: #FF5A50;
  border-color: #FF5A50;
  color: white;
}

.step-circle.current {
  border-color: #FF5A50;
  color: #FF5A50;
}

.step-circle.edge {
  border-color: #004650;
  background-color: white;
  color: #004650;
}

.step-circle.edge.completed {
  background-color: #FF5A50;
  border-color: #FF5A50;
  color: white;
}

.connector {
  height: 2px;
  background-color: #004650;
  flex-grow: 1;
  margin: 0 -1px;
  z-index: 1;
}

.connector.completed {
  background-color: #FF5A50;
}

.labels-row {
  display: flex;
  align-items: center;
  margin-top: 0.5rem;
  width: 100%;
  overflow-x: auto;
}

.label-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  flex-shrink: 0;
}

.label {
  text-align: center;
  font-size: 0.85rem;
  color: #004650;
  background-color: white;
  word-break: break-word;
}

.label.completed {
  color: #FF5A50;
  font-weight: 600;
}

.placeholder-label {
  width: 28px;
  visibility: hidden;
}

.connector-placeholder {
  flex-grow: 1;
}

/* Hides edge items on mobile */
.edge-mobile-hide {
  display: flex;
}

@media (max-width: 600px) {
  .step-circle {
    width: 24px;
    height: 24px;
    font-size: 12px;
  }

  .label {
    font-size: 0.75rem;
    max-width: 60px;
    white-space: normal;
    text-align: center;
  }

  .label-wrapper {
    width: auto;
    padding: 0 2px;
  }

  .wizard-container {
    padding: 1rem 0.5rem;
  }

  .edge-mobile-hide {
    display: none !important;
  }

  .first-connector {
    display: none !important;
  }
}
</style>
